import { Component, EventEmitter, Inject, OnInit, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DynamicFormService } from '../../../dynamic-form/services/dynamic-form.service';
import { VehiclesWithOrgType } from '../../../dynamic-form/components/vehicle-capacity/vehicle-capacity.component';
import { API } from '@configs/api.config';
export interface VehicleType {
  id: string;
  categoryName: string;
  maxLoadVolume: number;
  maxLoadWeight: number;
  costPerKm: number;
  /** Seeds the dialog's utilization control; callers pass the current global. Defaults to 100. */
  capacityUtilization?: number;
  dimensions: {
    length: number;
    width: number;
    height: number;
  };
}

export interface VehicleCategoryResponse {
  id: string;
  categoryName: string;
  maxLoadVolume?: number;
  maxLoadWeight?: number;
  costPerKm?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
}

@Component({
    selector: 'hmt-vehicle-count-modifier',
    templateUrl: './vehicle-count-modifier.component.html',
    styleUrls: ['./vehicle-count-modifier.component.scss'],
    standalone: false
})
export class VehicleCountModifierComponent implements OnInit, OnDestroy {
  @Output() modifiedCount = new EventEmitter<any>();
  form: FormGroup;
  title: string;
  showVehicleDetails: boolean;
  vehicleTypes: VehicleType[] = [];
  isEditing: boolean;
  isLoading = true;
  shouldFetchVehicleData = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dynamicFormService: DynamicFormService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<VehicleCountModifierComponent>
  ) {
    this.isEditing = data.isEditing || false;
    this.showVehicleDetails = data.showVehicleDetails || false;

    if (this.isEditing) {
      this.title = `Edit Standard Vehicle`;
    } else {
      this.title = 'Add Standard Vehicle';
    }

    this.form = this.fb.group({
      vehicleType: [data.vehicleType || null, Validators.required],
            count: [
        data.count || null,
        this.isEditing ?
          [Validators.required, Validators.min(0)] :
          [Validators.required, Validators.min(1)]
      ],
      vehicleDetails: this.fb.group({
        maxLoadVolume: [data.vehicleDetails?.maxLoadVolume || null, [Validators.required, Validators.min(0)]],
        maxLoadWeight: [data.vehicleDetails?.maxLoadWeight || null, [Validators.required, Validators.min(0)]],
        costPerKM: [data.vehicleDetails?.costPerKM || null, [Validators.required, Validators.min(0)]],
        capacityUtilization: [
          data.vehicleDetails?.capacityUtilization ?? 100,
          [Validators.required, Validators.min(0), Validators.max(100)]
        ],
        dimensions: this.fb.group({
          length: [data.vehicleDetails?.dimensions?.length || null],
          width: [data.vehicleDetails?.dimensions?.width || null],
          height: [data.vehicleDetails?.dimensions?.height || null]
        })
      })
    });

    if (this.isEditing) {
      this.form.get('vehicleType').disable();
      if (data.count === 0) {
        this.shouldFetchVehicleData = true;
      } else {
        this.form.patchValue({
          count: data.count || null,
          vehicleDetails: {
            maxLoadVolume: data.vehicleDetails?.maxLoadVolume || null,
            maxLoadWeight: data.vehicleDetails?.maxLoadWeight || null,
            costPerKM: data.vehicleDetails?.costPerKM || null,
            capacityUtilization: data.vehicleDetails?.capacityUtilization ?? 100,
            dimensions: {
              length: data.vehicleDetails?.dimensions?.length || null,
              width: data.vehicleDetails?.dimensions?.width || null,
              height: data.vehicleDetails?.dimensions?.height || null
            }
          }
        });
      }
    }
  }

  ngOnInit(): void {
    this.loadAvailableVehicles();

    this.form.get('count').valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (value !== null) {
        if (!this.isEditing && value < 1) {
          this.form.get('count').setValue(1, { emitEvent: false });
        } else if (this.isEditing && value < 0) {
          this.form.get('count').setValue(0, { emitEvent: false });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAvailableVehicles(): void {
    const queryParams = this.data.operationId ? { operationId: this.data.operationId } : {};

    const URL_1 = API.organization.getVehicleCategoryWithDimension;
    this.dynamicFormService.fetchValuesForDropdown(
      URL_1,
      false,
      false,
      {},
      queryParams
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (response: VehicleCategoryResponse[]) => {
        this.vehicleTypes = response.map(category => ({
          id: category.categoryName,
          categoryName: category.categoryName,
          maxLoadVolume: Number(category.maxLoadVolume.toFixed(3)) || null,
          maxLoadWeight: category.maxLoadWeight || null,
          costPerKm: null,
          dimensions: {
            length: 0,
            width: 0,
            height: 0
          }
        }));

        this.isLoading = false;

        if (!this.isEditing) {
          this.setupVehicleTypeChangeListener();
        } else if (this.shouldFetchVehicleData && this.data.vehicleType) {
          this.populateVehicleDetailsFromSelectedType(this.data.vehicleType);
        }

        if (this.isEditing && this.data.vehicleType) {
          this.form.get('vehicleType').setValue(this.data.vehicleType, { emitEvent: false });
        }
      },
      error => {
        console.error('Error loading vehicle categories:', error);
        this.isLoading = false;
        this.vehicleTypes = [];
      }
    );
  }

  setupVehicleTypeChangeListener(): void {
    this.form.get('vehicleType').valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(selectedType => {
      this.populateVehicleDetailsFromSelectedType(selectedType);
    });
  }

  populateVehicleDetailsFromSelectedType(selectedType: string): void {
    const selectedVehicle = this.vehicleTypes.find(v => v.id === selectedType);
    if (selectedVehicle) {
      this.form.get('vehicleDetails').patchValue({
        categoryName: selectedVehicle.categoryName,
        maxLoadVolume: selectedVehicle.maxLoadVolume || null,
        maxLoadWeight: selectedVehicle.maxLoadWeight || null,
        costPerKM: selectedVehicle.costPerKm || null,
        dimensions: {
          length: selectedVehicle.dimensions.length || 0,
          width: selectedVehicle.dimensions.width || 0,
          height: selectedVehicle.dimensions.height || 0
        }
      }, { emitEvent: false });
    }
  }

  increment(): void {
    const currentValue = this.form.get('count').value || 0;
    this.form.get('count').setValue(currentValue + 1);
  }

  onSliderInput(event: Event): void {
    // This fires continuously while dragging the slider
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    
    if (!isNaN(value)) {
      // Update the form control in real-time during drag
      this.form.get('vehicleDetails.capacityUtilization').setValue(value, { emitEvent: false });
    }
  }

  onCapacityInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    
    if (!isNaN(value)) {
      // Clamp the value between 0 and 100
      const clampedValue = Math.max(0, Math.min(100, value));
      this.form.get('vehicleDetails.capacityUtilization').setValue(clampedValue);
    }
  }

  decrement(): void {
    const currentValue = this.form.get('count').value || 1;
    if (currentValue > 1) {
      this.form.get('count').setValue(currentValue - 1);
    }
  }

  isSaveButtonDisabled(): boolean {
    if (!this.form.valid) {
      return true;
    }

    const count = this.form.get('count').value;

    if (!this.isEditing && (count === null || count < 1)) {
      return true;
    }

    if (this.isEditing && (count === null || count < 0)) {
      return true;
    }

    return false;
  }

  save(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();

      if (!this.isEditing && formValue.count < 1) {
        return;
      }

      if (this.isEditing && formValue.count < 0) {
        return;
      }

      if (formValue.count === null) {formValue.count = 0;}
      if (formValue.vehicleDetails.maxLoadVolume === null) {formValue.vehicleDetails.maxLoadVolume = 0;}
      if (formValue.vehicleDetails.maxLoadWeight === null) {formValue.vehicleDetails.maxLoadWeight = 0;}
      if (formValue.vehicleDetails.costPerKM === null) {formValue.vehicleDetails.costPerKM = 0;}
      if (formValue.vehicleDetails.dimensions.length === null) {formValue.vehicleDetails.dimensions.length = 0;}
      if (formValue.vehicleDetails.dimensions.width === null) {formValue.vehicleDetails.dimensions.width = 0;}
      if (formValue.vehicleDetails.dimensions.height === null) {formValue.vehicleDetails.dimensions.height = 0;}

      const selectedVehicle = this.vehicleTypes.find(v => v.id === formValue.vehicleType);
      if (selectedVehicle && !formValue.vehicleDetails.dimensions) {
        formValue.vehicleDetails.dimensions = {
          length: selectedVehicle.dimensions.length,
          width: selectedVehicle.dimensions.width,
          height: selectedVehicle.dimensions.height
        };
      }
      this.dialogRef.close(formValue);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
